/** Sub-routes under /home for Learn roadmap (browser + header nav history). */
import { normalizeRoadmapLanguageKey } from '../../utils/roadmapDisplay';

/** Marketing-friendly alias — canonical in-app param is `roadmapLang`. */
export const LANDING_LANGUAGE_SEARCH_PARAM = 'language';

/** Marketing-friendly alias — canonical in-app param is `roleId`. */
export const LANDING_ROLE_SEARCH_PARAM = 'role';

function parseRoadmapSearch(search) {
  if (!search) return new URLSearchParams();
  const raw = search.startsWith('?') ? search.slice(1) : search;
  return new URLSearchParams(raw);
}

/**
 * Read language from `language` (marketing) or `roadmapLang` (in-app).
 * @param {string} [search]
 * @returns {{ raw: string|null, normalized: string }}
 */
export function readLanguageParamFromSearch(search) {
  const params = parseRoadmapSearch(search);
  const fromMarketing = params.get(LANDING_LANGUAGE_SEARCH_PARAM);
  const fromApp = params.get(ROADMAP_GRAPH_LANG_SEARCH_PARAM);
  const raw =
    fromMarketing != null && String(fromMarketing).trim()
      ? String(fromMarketing).trim()
      : fromApp != null && String(fromApp).trim()
        ? String(fromApp).trim()
        : null;
  if (!raw) return { raw: null, normalized: '' };
  return { raw, normalized: normalizeRoadmapLanguageKey(raw) };
}

/**
 * Read role from `role` (marketing) or `roleId` (in-app).
 * @param {string} [search]
 * @returns {string|null}
 */
export function readRoleParamFromSearch(search) {
  const params = parseRoadmapSearch(search);
  const fromMarketing = params.get(LANDING_ROLE_SEARCH_PARAM);
  const fromApp = params.get(ROADMAP_ROLE_ID_SEARCH_PARAM);
  const raw =
    fromMarketing != null && String(fromMarketing).trim()
      ? String(fromMarketing).trim()
      : fromApp != null && String(fromApp).trim()
        ? String(fromApp).trim()
        : null;
  return raw || null;
}

export const ROADMAP_PATHS = {
  menu: '/home/roadmap',
  graph: '/home/roadmap/graph'
};

/** Query param on `/home/roadmap` for role-based learning (`roles/{id}` in Firestore). */
export const ROADMAP_ROLE_ID_SEARCH_PARAM = 'roleId';

/**
 * Learn hub URL with the Roles tab scoped to one role.
 * @param {string|null|undefined} roleId
 * @returns {string}
 */
export function buildRoleRoadmapLearnPath(roleId) {
  const id = roleId != null ? String(roleId).trim() : '';
  if (!id) return ROADMAP_PATHS.menu;
  const params = new URLSearchParams();
  params.set(ROADMAP_ROLE_ID_SEARCH_PARAM, id);
  return `${ROADMAP_PATHS.menu}?${params.toString()}`;
}

/**
 * Pass with `navigate(ROADMAP_PATHS.graph, { state: ROADMAP_GRAPH_NAV_FROM_HOME_STATE })`
 * (e.g. Home “Continue learning”) so the app header back button returns to Home instead of the language menu.
 */
export const ROADMAP_GRAPH_NAV_FROM_HOME_STATE = { roadmapNavFromHome: true };

/**
 * Pass with `navigate(ROADMAP_PATHS.graph, { state: ROADMAP_GRAPH_NAV_FROM_PROFILE_STATE })`
 * (e.g. profile track cards) so the header back button returns to Profile instead of the language menu.
 */
export const ROADMAP_GRAPH_NAV_FROM_PROFILE_STATE = { roadmapNavFromProfile: true };

/**
 * Set on navigate(menu → graph) so the chosen language wins even if Router updates location
 * before `useRoadmap` applies `setSelectedLanguage` (avoids forced fallback to last-visited lang).
 */
export const ROADMAP_GRAPH_MENU_SELECTED_LANG_STATE_KEY = 'roadmapMenuSelectedLang';

/** One-shot nav from profile track cards (consumed in RoadmapView). */
export const ROADMAP_GRAPH_SELECTED_TRACK_ID_STATE_KEY = 'roadmapSelectedTrackId';

/** Open track preview panel after selecting track (Home Continue learning). */
export const ROADMAP_GRAPH_OPEN_TRACK_PREVIEW_STATE_KEY = 'roadmapOpenTrackPreview';

/** Focus cookbook node id inside track preview (Home resume step). */
export const ROADMAP_GRAPH_RESUME_NODE_ID_STATE_KEY = 'roadmapResumeNodeId';

/** @param {unknown} state */
export function roadmapGraphStateWithoutMenuLang(state) {
  if (!state || typeof state !== 'object' || Array.isArray(state)) return undefined;
  if (!Object.prototype.hasOwnProperty.call(state, ROADMAP_GRAPH_MENU_SELECTED_LANG_STATE_KEY)) {
    return state;
  }
  const { [ROADMAP_GRAPH_MENU_SELECTED_LANG_STATE_KEY]: _, ...rest } = state;
  return Object.keys(rest).length > 0 ? rest : undefined;
}

/** @param {unknown} state */
export function roadmapGraphStateWithoutSelectedTrack(state) {
  if (!state || typeof state !== 'object' || Array.isArray(state)) return undefined;
  if (!Object.prototype.hasOwnProperty.call(state, ROADMAP_GRAPH_SELECTED_TRACK_ID_STATE_KEY)) {
    return state;
  }
  const { [ROADMAP_GRAPH_SELECTED_TRACK_ID_STATE_KEY]: _, ...rest } = state;
  return Object.keys(rest).length > 0 ? rest : undefined;
}

/** Strip one-shot Continue-learning deep-link keys from graph nav state. */
export function roadmapGraphStateWithoutContinueDeepLink(state) {
  if (!state || typeof state !== 'object' || Array.isArray(state)) return undefined;
  const {
    [ROADMAP_GRAPH_SELECTED_TRACK_ID_STATE_KEY]: _track,
    [ROADMAP_GRAPH_OPEN_TRACK_PREVIEW_STATE_KEY]: _preview,
    [ROADMAP_GRAPH_RESUME_NODE_ID_STATE_KEY]: _node,
    ...rest
  } = state;
  return Object.keys(rest).length > 0 ? rest : undefined;
}

/**
 * Query key on `/home/roadmap/graph` so the chosen language survives refresh and Router/state ordering.
 * Value is lowercase (see `normalizeRoadmapLanguageKey` in `utils/roadmapDisplay.js`).
 */
export const ROADMAP_GRAPH_LANG_SEARCH_PARAM = 'roadmapLang';

/**
 * @param {string} search - `location.search` (typically starts with `?`)
 * @param {string[]|null|undefined} availableLanguages
 * @returns {string|null}
 */
export function matchRoadmapGraphLangSearch(search, availableLanguages) {
  if (!search || !availableLanguages?.length) return null;
  try {
    const { normalized: norm } = readLanguageParamFromSearch(search);
    if (!norm) return null;
    return availableLanguages.find((l) => normalizeRoadmapLanguageKey(l) === norm) ?? null;
  } catch {
    return null;
  }
}
